import { TABLES_SYNCHRONISEES } from '../../../db/schema.ts'
import { uuidv7 } from '../ids.ts'
import { appliquerOp, type Op, type Table } from '../ops/index.ts'
import {
  etatVide,
  cloner,
  lecture,
  appliquerMutations,
  type EtatMemoire,
} from '../ops/memoire.ts'
import * as idb from './idb.ts'

/**
 * Le magasin client — miroir, outbox, et pli dérivé.
 *
 * TROIS RÈGLES, dans l'ordre d'importance.
 *
 * 1. L'ÉTAT AFFICHÉ EST DÉRIVÉ, JAMAIS STOCKÉ.
 *    `affiché = fold(appliquerOp, lignesServeur, outbox)`. Le miroir ne
 *    contient que des lignes confirmées par le serveur ; l'outbox contient
 *    les intentions pas encore accusées. On recalcule à chaque mutation.
 *    C'est ce qui élimine la pire classe de bugs du hors-ligne : la
 *    divergence silencieuse entre une écriture optimiste locale et la vérité
 *    serveur. Le volume (quelques milliers de lignes) rend ce pli gratuit.
 *
 * 2. L'OUTBOX EST LA SEULE DONNÉE DONT LA PERTE EST UNE PERTE.
 *    Le miroir est toujours re-téléchargeable ; l'outbox, non. Elle n'est
 *    jamais vidée sans accusé explicite du serveur, et aucune migration ne
 *    la touche.
 *
 * 3. L'UI N'APPELLE JAMAIS `fetch`, ELLE APPELLE `dispatch`.
 *    C'est le seul point de couture. Les écrans ne savent pas s'il y a du
 *    réseau, et n'ont pas à le savoir.
 */

export type EntreeOutbox = Op & { creeLe: number }

export type Conflit = {
  opId: string
  raison: string
  message: string
  le: number
}

export type Instantane = {
  etat: EtatMemoire
  /** Nombre d'opérations pas encore accusées. Alimente la pastille. */
  enAttente: number
  /** Dernière tentative de synchronisation en échec. */
  echec: boolean
  conflits: Conflit[]
  pret: boolean
  rev: number
}

type Abonne = () => void

class Magasin {
  /** Lignes CONFIRMÉES par le serveur. Jamais modifiées de façon optimiste. */
  private miroir: EtatMemoire = etatVide()
  private outbox: EntreeOutbox[] = []
  private conflits: Conflit[] = []
  private abonnes = new Set<Abonne>()

  private rev = 0
  private deviceId = ''
  private deviceSeq = 0
  /** Décalage d'horloge mesuré sur le serveur. Voir `maintenant()`. */
  private offsetHorloge = 0
  private userId = ''
  private prenom = ''
  private householdId = 'foyer'

  private pret = false
  private echec = false
  private synchroEnCours: Promise<void> | null = null
  private instantane: Instantane | null = null

  /* ---------------------------------------------------------------- */
  /* Abonnement (useSyncExternalStore)                                 */
  /* ---------------------------------------------------------------- */

  abonner = (f: Abonne) => {
    this.abonnes.add(f)
    return () => this.abonnes.delete(f)
  }

  lire = (): Instantane => {
    if (!this.instantane) this.recalculer()
    return this.instantane!
  }

  private notifier() {
    this.recalculer()
    for (const f of this.abonnes) f()
  }

  /**
   * LE PLI. Recalcule l'état visible depuis le miroir serveur et l'outbox.
   *
   * On travaille sur une COPIE du miroir : appliquer l'outbox dessus le
   * corromprait, et le prochain pull ne saurait plus ce qui vient du serveur.
   */
  private recalculer() {
    const etat = cloner(this.miroir)
    const compteur = { seq: this.rev }
    const ctx = {
      householdId: this.householdId,
      userId: this.userId,
      maintenant: this.maintenant(),
      revCourant: this.rev,
    }

    for (const op of this.outbox) {
      const r = appliquerOp(op, lecture(etat), ctx)
      appliquerMutations(etat, r.mutations, ctx, () => ++compteur.seq)
    }

    this.instantane = {
      etat,
      enAttente: this.outbox.length,
      echec: this.echec,
      conflits: this.conflits,
      pret: this.pret,
      rev: this.rev,
    }
  }

  /**
   * L'heure, corrigée du décalage serveur.
   *
   * Les horloges de téléphone dérivent, et parfois de beaucoup. Ça
   * n'intervient jamais dans l'ordre des opérations — `rev` et `deviceSeq`
   * s'en chargent — mais ça compte pour les DLC : un téléphone en avance de
   * trois jours afficherait la moitié du congélateur en rouge.
   */
  maintenant(): Date {
    return new Date(Date.now() + this.offsetHorloge)
  }

  /* ---------------------------------------------------------------- */
  /* Démarrage                                                         */
  /* ---------------------------------------------------------------- */

  async demarrer(userId: string, householdId: string, prenom?: string) {
    this.userId = userId
    this.householdId = householdId
    this.prenom = prenom ?? ''

    this.deviceId = await idb.lireMeta('deviceId', '')
    if (!this.deviceId) {
      this.deviceId = uuidv7()
      await idb.ecrireMeta('deviceId', this.deviceId)
    }
    this.rev = await idb.lireMeta('rev', 0)
    this.deviceSeq = await idb.lireMeta('deviceSeq', 0)
    this.offsetHorloge = await idb.lireMeta('offsetHorloge', 0)

    this.outbox = await idb.tousLes<EntreeOutbox>('outbox')
    this.outbox.sort((a, b) => a.deviceSeq - b.deviceSeq)
    this.conflits = await idb.tousLes<Conflit>('conflits')

    for (const nom of TABLES_SYNCHRONISEES) {
      for (const ligne of await idb.tousLes<{ id: string }>(nom)) {
        this.miroir[nom].set(ligne.id, ligne as never)
      }
    }

    this.pret = true
    this.notifier()

    /*
     * Le stockage persistant se demande APRÈS le premier rendu : c'est une
     * assurance, pas une condition de démarrage. Safari exempte déjà les
     * applications ajoutées à l'écran d'accueil de la purge à 7 jours, mais
     * la demande explicite couvre le cas de l'onglet non installé, et elle
     * ne coûte rien.
     */
    void navigator.storage?.persist?.()

    this.installerDeclencheurs()
    void this.synchroniser().then(() => this.seRattacherAuFoyer())
  }

  /**
   * Inscrit le compte connecté comme PERSONNE du foyer, s'il n'y est pas.
   *
   * Un compte et une personne sont deux choses distinctes : les enfants ont
   * une place à table sans avoir de mot de passe. Mais créer un compte sans
   * apparaître dans le foyer était un trou — l'assistant ignorait jusqu'à
   * l'existence de celui qui lui parlait, et il fallait se ressaisir
   * soi-même dans les réglages.
   *
   * Passe par `dispatch`, donc par le journal d'opérations : tous les autres
   * téléphones apprennent la nouvelle personne à leur prochaine synchro.
   */
  private seRattacherAuFoyer() {
    if (!this.prenom) return
    const deja = [...this.miroir.people.values()].some(
      (p) => !p.deletedAt && p.userId === this.userId,
    )
    if (deja) return
    this.dispatch('person.upsert', {
      id: uuidv7(),
      name: this.prenom,
      userId: this.userId,
    })
  }

  /* ---------------------------------------------------------------- */
  /* Le seul point d'entrée des écrans                                 */
  /* ---------------------------------------------------------------- */

  /**
   * Enregistre une intention et rend la main IMMÉDIATEMENT.
   *
   * Aucun await sur le réseau : c'est ce qui fait qu'un tap reste instantané
   * dans un sous-sol de supermarché.
   */
  dispatch = (kind: string, payload: Record<string, unknown>): void => {
    const op: EntreeOutbox = {
      opId: uuidv7(),
      deviceSeq: ++this.deviceSeq,
      baseRev: this.rev,
      clientAt: Date.now(),
      kind,
      payload,
      creeLe: Date.now(),
    }
    this.outbox.push(op)
    this.notifier()

    void idb
      .ecrire(['outbox', 'meta'], (tx) => {
        tx.objectStore('outbox').put(op)
        tx.objectStore('meta').put({ cle: 'deviceSeq', valeur: this.deviceSeq })
      })
      .then(() => this.planifierSynchro())
  }

  /** Nouvel identifiant, généré côté client — voir src/lib/ids.ts. */
  id = () => uuidv7()

  /* ---------------------------------------------------------------- */
  /* Synchronisation                                                   */
  /* ---------------------------------------------------------------- */

  private minuterie: ReturnType<typeof setTimeout> | null = null

  /** Débat de 400 ms : regroupe la rafale de coches du mode magasin. */
  private planifierSynchro() {
    if (this.minuterie) clearTimeout(this.minuterie)
    this.minuterie = setTimeout(() => void this.synchroniser(), 400)
  }

  private installerDeclencheurs() {
    /*
     * `visibilitychange` est LE déclencheur qui compte, et le seul qui marche
     * partout : c'est lui qui fait que sortir le téléphone de sa poche en
     * rentrant du magasin déclenche l'envoi.
     *
     * iOS n'a JAMAIS implémenté la Background Sync API. On ne s'y adosse donc
     * pas ; elle n'est qu'un bonus sur Android, et rien ne doit en dépendre.
     */
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.synchroniser()
    })
    window.addEventListener('online', () => void this.synchroniser())

    /*
     * Le battement de 60 s RESTE, malgré le flux temps réel.
     *
     * Le flux est une optimisation de latence, jamais une source de vérité :
     * une connexion peut être coupée par un proxy, un changement de réseau,
     * une mise en veille d'iOS. Si la justesse des données dépendait de lui,
     * chaque coupure deviendrait une divergence silencieuse — exactement le
     * mode d'échec qu'on vient de passer deux jours à corriger.
     */
    setInterval(() => {
      if (document.visibilityState === 'visible') void this.synchroniser()
    }, 60_000)

    this.ouvrirFlux()
    // iOS suspend les connexions en arrière-plan sans toujours émettre
    // d'erreur : au retour au premier plan, on repart d'un flux neuf plutôt
    // que d'espérer que l'ancien vive encore.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.ouvrirFlux()
    })
  }

  private flux: EventSource | null = null

  /**
   * Le flux temps réel : une sonnette, pas un canal de données.
   *
   * Le serveur n'envoie aucune ligne — seulement « quelque chose a bougé ».
   * On rappelle alors `/api/sync`, le chemin unique, paginé et éprouvé.
   * Pousser l'état par ici créerait un SECOND chemin de lecture à tenir
   * d'accord avec le premier, et c'est précisément la classe de bugs que
   * toute l'architecture évite.
   *
   * `EventSource` se reconnecte tout seul, avec un recul progressif. C'est
   * la raison principale de le préférer à un WebSocket ici : on ne veut pas
   * écrire une machine à reconnexion pour un foyer de trois personnes.
   */
  private ouvrirFlux() {
    if (typeof EventSource === 'undefined') return
    // Une connexion déjà vivante ne se remplace pas : on la rouvrirait à
    // chaque retour au premier plan, pour rien.
    if (this.flux && this.flux.readyState !== EventSource.CLOSED) return

    this.flux?.close()
    const es = new EventSource(`/api/sync/flux?deviceId=${encodeURIComponent(this.deviceId)}`)
    this.flux = es
    es.addEventListener('changement', () => void this.synchroniser())
    es.addEventListener('error', () => {
      /*
       * On ne fait RIEN, et surtout pas passer la pastille en échec. Une
       * coupure de flux n'est pas une perte de données : le battement et le
       * retour au premier plan rattrapent. Crier ici reviendrait à alerter
       * sur la vie normale d'une connexion longue sur un réseau mobile.
       */
    })
  }

  private rappelDemande = false

  synchroniser = async (): Promise<void> => {
    /*
     * Une seule synchro en vol : deux pushes concurrents se marcheraient
     * dessus sur le curseur.
     *
     * Mais une demande arrivée PENDANT une synchro ne doit pas être perdue.
     * Elle l'était : le garde rendait la promesse courante, et la sonnette
     * reçue entre-temps disparaissait — le changement n'arrivait alors
     * qu'au battement suivant, jusqu'à soixante secondes plus tard. Un
     * temps réel qui rate une fois sur deux est pire qu'un temps réel
     * assumé comme différé, parce qu'on cesse de savoir à quoi s'attendre.
     *
     * On note donc la demande et on relance une fois la synchro finie.
     */
    if (this.synchroEnCours) {
      this.rappelDemande = true
      return this.synchroEnCours
    }
    this.synchroEnCours = this.faireSynchro().finally(() => {
      this.synchroEnCours = null
      if (this.rappelDemande) {
        this.rappelDemande = false
        void this.synchroniser()
      }
    })
    return this.synchroEnCours
  }

  private async faireSynchro(): Promise<void> {
    /*
     * BOUCLE, et non rappel récursif de `synchroniser()`.
     *
     * La pagination et la réinitialisation se relançaient par
     * `this.synchroniser()` — qui refuse de partir tant qu'une synchro est
     * EN VOL. Or on l'appelait depuis l'intérieur de celle-ci : le garde
     * rendait la promesse courante et la relance ne se produisait jamais.
     * Le client ne recevait donc qu'UNE page par réveil de l'app, et
     * rouvrir Popote faisait avancer d'un cran — d'où ce foyer qui
     * rouvrait trois fois de suite sans jamais voir son inventaire complet.
     *
     * Le garde reste nécessaire : deux synchros concurrentes se
     * marcheraient dessus sur le curseur. C'est l'appel récursif qui était
     * faux, pas lui.
     *
     * La borne de 200 tours n'est pas décorative : si le serveur cessait
     * de faire progresser le curseur, une boucle `while (!done)` gèlerait
     * l'onglet. On préfère s'arrêter et réessayer au réveil suivant.
     */
    /*
     * Une réinitialisation par session de synchro, pas davantage.
     *
     * Elle signifie « ton curseur est trop vieux, repars de zéro ». En
     * recevoir une SECONDE juste après être reparti de zéro veut dire que
     * le serveur et le client ne s'entendent pas — et la boucle qui en
     * résulte vide puis remplit le miroir sans fin, en martelant le réseau.
     * C'est arrivé : 219 requêtes en dix secondes, l'écran qui clignote.
     *
     * On s'arrête donc, et on réessaiera au réveil suivant. Les données
     * locales restent celles de la dernière page reçue : incomplet vaut
     * mieux qu'un écran qui se vide en boucle.
     */
    let reinitialisations = 0

    for (let page = 0; page < 200; page++) {
    const envoi = this.outbox.slice(0, 200)
    try {
      const reponse = await fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: this.deviceId,
          since: this.rev,
          clientAt: Date.now(),
          ops: envoi.map(({ creeLe: _creeLe, ...op }) => op),
        }),
      })

      if (reponse.status === 401) {
        /*
         * Session expirée. On NE SUPPRIME RIEN : les opérations restent dans
         * l'outbox et repartiront après reconnexion. Perdre les courses de
         * quelqu'un parce qu'un cookie a expiré serait le pire échec possible.
         */
        this.echec = true
        this.notifier()
        return
      }
      if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`)

      const data = (await reponse.json()) as {
        serverTime: number
        rev: number
        done: boolean
        reset?: boolean
        applied: string[]
        rejected: Conflit[]
        adjusted: Conflit[]
        changes: Record<string, unknown[]>
      }

      this.offsetHorloge = data.serverTime - Date.now()

      if (data.reset) {
        /*
         * Curseur trop vieux : le serveur ne peut plus dire ce qui a été
         * supprimé. On repart de zéro — mais on POUSSE D'ABORD, ce que
         * l'endpoint unique fait naturellement puisque les opérations sont
         * traitées avant le calcul du delta. On ne vide que les miroirs.
         *
         * La SECONDE réinitialisation d'une même session s'arrête AVANT
         * d'effacer. Vider une deuxième fois ne rendrait pas la boucle plus
         * courte, ça laisserait juste l'écran vide — alors qu'on détient
         * déjà la page qu'on vient de recevoir. Mieux vaut un inventaire
         * incomplet qu'un inventaire vide.
         */
        if (reinitialisations >= 1) return
        reinitialisations += 1
        await idb.viderMiroirs()
        this.miroir = etatVide()
        this.rev = 0
        await idb.ecrireMeta('rev', 0)
        this.echec = false
        this.notifier()
        continue
      }

      const accusees = new Set([
        ...data.applied,
        ...data.rejected.map((r) => r.opId),
        ...data.adjusted.map((a) => a.opId),
      ])

      const nouveauxConflits: Conflit[] = [...data.rejected, ...data.adjusted].map((c) => ({
        ...c,
        le: Date.now(),
      }))

      // Miroir : on écrase par les lignes serveur, pierres tombales comprises.
      for (const nom of TABLES_SYNCHRONISEES) {
        for (const ligne of (data.changes[nom] ?? []) as Array<{ id: string }>) {
          this.miroir[nom].set(ligne.id, ligne as never)
        }
      }

      this.outbox = this.outbox.filter((o) => !accusees.has(o.opId))
      this.conflits = [...nouveauxConflits, ...this.conflits].slice(0, 20)
      this.rev = data.rev
      this.echec = false

      await idb.ecrire([...TABLES_SYNCHRONISEES, 'outbox', 'conflits', 'meta'], (tx) => {
        for (const nom of TABLES_SYNCHRONISEES) {
          const magasin = tx.objectStore(nom)
          for (const ligne of (data.changes[nom] ?? []) as Array<{ id: string }>) magasin.put(ligne)
        }
        const boite = tx.objectStore('outbox')
        for (const opId of accusees) boite.delete(opId)
        const conflitsStore = tx.objectStore('conflits')
        for (const c of nouveauxConflits) conflitsStore.put(c)
        tx.objectStore('meta').put({ cle: 'rev', valeur: this.rev })
        tx.objectStore('meta').put({ cle: 'offsetHorloge', valeur: this.offsetHorloge })
      })

      this.notifier()

      // Pagination : le serveur avait plus à livrer, on enchaîne.
      if (data.done) return
    } catch {
      /*
       * ÊTRE HORS-LIGNE N'EST PAS UN ÉCHEC.
       *
       * C'est le fonctionnement NORMAL d'une app local-first, et la pastille
       * doit rester grise : la colorer reviendrait à crier à chaque passage
       * en sous-sol de supermarché, c'est-à-dire exactement là où l'app est
       * censée être sereine. La règle de couleur de l'app s'applique ici
       * aussi — la couleur suit l'ACTIONNABILITÉ.
       *
       * On ne passe en ambre que si le navigateur se dit EN LIGNE et que la
       * synchro échoue quand même (serveur en panne, session expirée), ou si
       * un geste traîne depuis plus de 24 h. Là, il y a quelque chose à faire.
       */
      const tropVieux = this.outbox.some((o) => Date.now() - o.creeLe > 24 * 60 * 60 * 1000)
      this.echec = this.outbox.length > 0 && (navigator.onLine || tropVieux)
      this.notifier()
      return
    }
    }
  }

  ecarterConflit = (opId: string) => {
    this.conflits = this.conflits.filter((c) => c.opId !== opId)
    void idb.ecrire(['conflits'], (tx) => tx.objectStore('conflits').delete(opId))
    this.notifier()
  }

  /**
   * La file d'attente, en français lisible.
   *
   * On expose des libellés et non les opérations brutes : une pastille
   * qu'on ouvre pour y lire « item.setState » n'est pas plus honnête qu'une
   * pastille qu'on ne peut pas ouvrir.
   */
  fileLisible = (): Array<{ opId: string; texte: string; le: number }> =>
    this.outbox.map((op) => ({
      opId: op.opId,
      texte: this.decrire(op),
      le: op.creeLe,
    }))

  private decrire(op: Op): string {
    const nomDe = (table: Table, id: unknown): string => {
      const l = this.miroir[table].get(String(id))
      if (!l) return 'un article'
      if (table === 'inventoryItems') {
        const p = this.miroir.catalogItems.get(String(l.catalogItemId))
        return String(p?.name ?? 'un article')
      }
      return String(l.name ?? l.freeText ?? 'un article')
    }
    const p = op.payload
    switch (op.kind) {
      case 'item.setState': {
        const etats: Record<string, string> = {
          en_stock: 'en stock',
          bientot_fini: 'bientôt fini',
          epuise: 'épuisé',
        }
        return `${nomDe('inventoryItems', p.id)} → ${etats[String(p.state)] ?? p.state}`
      }
      case 'item.create':
        return `${nomDe('catalogItems', p.catalogItemId)} ajouté à l'inventaire`
      case 'item.setNote':
        return `Note sur ${nomDe('inventoryItems', p.id)}`
      case 'item.move':
        return `${nomDe('inventoryItems', p.id)} déplacé`
      case 'expiry.add':
        return `Péremption ${p.date} sur ${nomDe('inventoryItems', p.inventoryItemId)}`
      case 'expiry.remove':
        return 'Date de péremption retirée'
      case 'item.delete':
        return `${nomDe('inventoryItems', p.id)} retiré de l'inventaire`
      case 'list.add':
        return `+ ${p.freeText ?? nomDe('catalogItems', p.catalogItemId)} aux courses`
      case 'list.setQuantity':
        return `Quantité de ${nomDe('shoppingItems', p.id)}`
      case 'list.check':
        return `${nomDe('shoppingItems', p.id)} ${p.checked ? 'coché' : 'décoché'}`
      case 'list.remove':
        return `${nomDe('shoppingItems', p.id)} retiré des courses`
      case 'trip.validate':
        return `Courses validées (${(p.itemIds as unknown[])?.length ?? 0} articles)`
      case 'catalog.create':
        return `Nouveau produit : ${p.name}`
      case 'recipe.upsert':
        return `Recette : ${p.title}`
      case 'plan.set':
        return `Menu du ${p.date} (${p.slot})`
      case 'pref.upsert':
        return 'Préférence alimentaire'
      default:
        return op.kind
    }
  }

  /** Lecture directe d'une table de l'état DÉRIVÉ. */
  table<T = Record<string, unknown>>(nom: Table): T[] {
    return [...this.lire().etat[nom].values()].filter((l) => !l.deletedAt) as T[]
  }
}

export const magasin = new Magasin()
